import {
  Link,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from '@remix-run/react'
import { getSite } from '~/sanity'
import { Header, Footer, Button, Heading } from '~/components'

// Styles
import styles from '~/tailwind.css?url'

// Type declarations
import type { LinksFunction, LoaderFunction } from '@remix-run/node'
import { useCustomScripts } from './hooks'
import { getGlobal } from './utils'

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'stylesheet', href: 'https://use.typekit.net/ubt6xee.css' },
  {
    rel: 'icon',
    href: '/favicon.png',
    type: 'image/png',
  },
] // Loader data
export const loader: LoaderFunction = async ({ request }) => {
  // Global env variables
  const { pathname, search } = new URL(request.url)
  const env = process.env

  // Redirect for trailing slash URLs
  if (pathname.endsWith('/') && pathname !== '/') {
    return redirect(`${pathname.slice(0, -1)}${search}`, 301)
  }

  // Cookie data
  // const cookieHeader = request.headers.get('Cookie')

  // Session & cookie data
  // const session = await getSession(request.headers.get('Cookie'))

  // Fetch global data
  const site = await getSite({
    dataset: env.ENVIRONMENT,
    projectId: env.SANITY_PROJECT,
  })

  return Response.json({
    site,
    env: {
      ENVIRONMENT: env?.ENVIRONMENT,
      SANITY_PROJECT: env?.SANITY_PROJECT,
      SITE_URL: env?.SITE_URL,
      RECAPTCHA_SITE_KEY: env?.RECAPTCHA_SITE_KEY,
    },
  })
}

// Error boundary
export function ErrorBoundary() {
  const error = useRouteError()

  console.error(error)

  return (
    <html>
      <head>
        <Meta />
        <Links />
      </head>
      <body>
        <div className="flex h-screen w-screen items-center justify-center text-center">
          <div className="container mx-auto">
            <div className="flex-column flex justify-center">
              <div className="w-full lg:w-10/12 xl:w-8/12">
                <Heading
                  tag="h1"
                  size="h1"
                  className="mb-16"
                >
                  Sorry, the page you were looking for cannot be found
                </Heading>
                <Link
                  to="/"
                  title="Back to home"
                  className="border border-transparent bg-gold-500 px-8 py-3 text-center text-beige-200 hover:border-gold-300 hover:bg-gold-300 hover:text-beige-200"
                >
                  Back to home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { env, site } = getGlobal()
  // Init custom scripts
  useCustomScripts({
    gtmId: site?.settings?.gtmId,
    recaptchaKey: env?.RECAPTCHA_SITE_KEY,
  })
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
        <Meta />
        <Links />
      </head>
      <body>
        <Header />
        {children}
        <Footer />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App() {
  return <Outlet />
}
